import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import {
  RefreshCw, Search, Download, ExternalLink, ArrowUp, ArrowDown, ImageOff, Info, CalendarDays, AlertTriangle, X, Star,
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

// ---------------------------------------------------------------------------
// Yesterday — dün ne sattık, hangi ürün hangi kanaldan geldi, hangisi getirmedi.
// Veri GET /api/high5/yesterday (GA4 BigQuery purchase olayları). Tarih
// verilmezse backend dünü döner.
// ---------------------------------------------------------------------------

const fmtInt = (n) => Number(n || 0).toLocaleString('tr-TR');
const fmtMoney = (n) => `${Math.round(Number(n) || 0).toLocaleString('tr-TR')} ₺`;
const fmtMoney2 = (n) => `${(Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
const fmtRoas = (n) => (n == null ? '—' : Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtDay = (d) => {
  if (!d) return '—';
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' });
  } catch { return d; }
};
const fmtShort = (d) => {
  try { return new Date(`${d}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }); } catch { return d || ''; }
};
const pctChange = (cur, prev) => (prev > 0 ? ((Number(cur) || 0) - prev) / prev * 100 : null);
const todayIst = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });

const PAGE = 50;

function Delta({ cur, prev, inverse = false }) {
  const v = pctChange(cur, prev);
  if (v == null) return <span className="text-gray-300">—</span>;
  const up = v >= 0;
  const good = inverse ? !up : up;
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums ${good ? 'text-emerald-700' : 'text-rose-700'}`}>
      <Icon className="w-3 h-3" />
      %{Math.abs(v).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}
    </span>
  );
}

function Thumb({ src, alt, onPreview }) {
  const [broken, setBroken] = useState(false);
  if (!src || broken) {
    return (
      <div className="w-12 h-12 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center shrink-0">
        <ImageOff className="w-4 h-4 text-gray-400" />
      </div>
    );
  }
  return (
    <img
      src={src} alt={alt} loading="lazy" onError={() => setBroken(true)}
      onMouseEnter={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        onPreview({ src, x: r.right + 8, y: Math.min(r.top, window.innerHeight - 248) });
      }}
      onMouseLeave={() => onPreview(null)}
      className="w-12 h-12 rounded-lg object-contain bg-white border border-gray-200 shrink-0 cursor-zoom-in"
    />
  );
}

function KpiCard({ label, value, sub, cur, prev, week, inverse, fmt }) {
  return (
    <div className="px-5 py-4">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-xl font-semibold text-gray-900 tabular-nums mt-1">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5 leading-snug">{sub}</div>}
      <div className="mt-2 space-y-0.5 text-[11px] text-gray-500">
        <div className="flex items-center gap-1.5">
          <span className="w-16">Önceki gün</span><Delta cur={cur} prev={prev} inverse={inverse} />
          <span className="text-gray-400 tabular-nums">{prev != null ? fmt(prev) : ''}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-16">Geçen hafta</span><Delta cur={cur} prev={week} inverse={inverse} />
          <span className="text-gray-400 tabular-nums">{week != null ? fmt(week) : ''}</span>
        </div>
      </div>
    </div>
  );
}

function BreakdownTable({ title, rows, labelKey, active, onPick, showProducts = true }) {
  const max = Math.max(1, ...rows.map(r => r.revenue));
  return (
    <div className="min-w-0">
      <div className="px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-50 border-b border-gray-100">{title}</div>
      <div className="max-h-72 overflow-y-auto">
        <table className="w-full text-xs">
          <tbody>
            {rows.map(r => {
              const key = r[labelKey];
              const on = active === key;
              return (
                <tr
                  key={key} onClick={() => onPick(on ? '' : key)}
                  className={`cursor-pointer border-b border-gray-50 ${on ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}
                >
                  <td className="px-4 py-1.5 text-gray-900 whitespace-nowrap">{key}</td>
                  <td className="px-2 py-1.5 w-24">
                    <div className="h-1.5 rounded bg-gray-100">
                      <div className="h-1.5 rounded bg-indigo-400" style={{ width: `${(r.revenue / max) * 100}%` }} />
                    </div>
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-900 whitespace-nowrap">{fmtMoney(r.revenue)}</td>
                  <td className="px-4 py-1.5 text-right tabular-nums text-gray-500 whitespace-nowrap">
                    {fmtInt(r.qty)} adet{showProducts && r.products != null ? ` · ${fmtInt(r.products)} ürün` : ''}
                  </td>
                </tr>
              );
            })}
            {!rows.length && <tr><td className="px-4 py-3 text-gray-400">Veri yok</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function High5Yesterday() {
  const { selectedBrand } = useAuth();
  const [date, setDate] = useState(null); // null = backend'in dünü
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [q, setQ] = useState('');
  const [fBrand, setFBrand] = useState('');
  const [fChannel, setFChannel] = useState('');
  const [fGender, setFGender] = useState('');
  const [fSize, setFSize] = useState('');
  const [sortKey, setSortKey] = useState('revenue');
  const [sortDir, setSortDir] = useState('desc');
  const [limit, setLimit] = useState(PAGE);
  const [preview, setPreview] = useState(null);

  const fetchData = useCallback(async (refresh = false) => {
    if (!selectedBrand?.id) { setData(null); return; }
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams();
      if (date) qs.set('date', date);
      if (refresh) qs.set('refresh', '1');
      const r = await fetch(`${API_BASE}/api/high5/yesterday${qs.toString() ? `?${qs}` : ''}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id },
      });
      const body = await r.json().catch(() => null);
      if (!r.ok) throw new Error(body?.error || `HTTP ${r.status}`);
      setData(body);
    } catch (e) {
      setError(e.message); setData(null);
    } finally { setLoading(false); }
  }, [selectedBrand?.id, date]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setLimit(PAGE); }, [q, fBrand, fChannel, fGender, fSize, sortKey, sortDir, date]);

  const downloadXlsx = async () => {
    if (!selectedBrand?.id || !data?.date) return;
    setExporting(true);
    try {
      const r = await fetch(`${API_BASE}/api/high5/yesterday?date=${data.date}&format=xlsx`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `high5-dun-${data.date}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      alert(`Dosya hazırlanamadı: ${e.message}`);
    } finally { setExporting(false); }
  };

  const products = useMemo(() => data?.products || [], [data]);
  const channels = useMemo(() => data?.channels || [], [data]);
  const s = data?.summary;

  const top5 = useMemo(() => new Set([...products].sort((a, b) => b.revenue - a.revenue).slice(0, 5).map(p => p.itemId)), [products]);

  const filterOptions = useMemo(() => {
    const uniq = (arr) => [...new Set(arr.filter(Boolean))];
    return {
      brands: uniq(products.map(p => p.brand)),
      genders: uniq(products.map(p => p.gender)),
    };
  }, [products]);

  const sizeOptions = useMemo(() => (data?.breakdowns?.sizes || []).map(x => x.size), [data]);

  const rows = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase('tr-TR');
    let list = products.filter(p => {
      if (fBrand && p.brand !== fBrand) return false;
      if (fGender && p.gender !== fGender) return false;
      if (fChannel && !(p.channels || []).some(c => c.channel === fChannel)) return false;
      if (fSize && !(p.sizes || []).some(z => z.size === fSize)) return false;
      if (!needle) return true;
      return [p.name, p.itemId, p.brand, p.color, p.category].some(v => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    });
    const val = (p) => {
      switch (sortKey) {
        case 'name': return p.name || '';
        case 'brand': return p.brand || '';
        case 'gender': return p.gender || '';
        case 'qty': return p.qty;
        case 'delta': { const d = pctChange(p.revenue, p.prevDay?.revenue || 0); return d == null ? -Infinity : d; }
        default: return p.revenue;
      }
    };
    const dir = sortDir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      const va = val(a); const vb = val(b);
      if (typeof va === 'string') return va.localeCompare(vb, 'tr') * dir;
      return (va - vb) * dir || (b.revenue - a.revenue);
    });
    return list;
  }, [products, q, fBrand, fGender, fChannel, fSize, sortKey, sortDir]);

  const filtered = !!(q || fBrand || fGender || fChannel || fSize);
  const onSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir(['name', 'brand', 'gender'].includes(key) ? 'asc' : 'desc'); }
  };
  const SortMark = ({ k }) => (sortKey === k
    ? (sortDir === 'desc' ? <ArrowDown className="w-3 h-3 inline" /> : <ArrowUp className="w-3 h-3 inline" />)
    : null);
  const clearFilters = () => { setQ(''); setFBrand(''); setFChannel(''); setFGender(''); setFSize(''); };

  const noSalesChannels = channels.filter(c => c.noSales || !(c.revenue > 0));
  const visible = rows.slice(0, limit);
  const maxDate = todayIst();
  const th = 'bg-gray-50 font-medium px-3 py-2 border-b border-gray-200 whitespace-nowrap';
  const selectCls = 'py-1.5 px-2 text-sm border border-gray-200 rounded-xl bg-white';

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <Card className="rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/70 to-white">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl border border-indigo-200 bg-indigo-50 flex items-center justify-center">
                <CalendarDays className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Yesterday</h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  Ürün, kanal, marka, cinsiyet ve beden bazında günlük satış
                  {data?.date ? <> · <strong>{fmtDay(data.date)}</strong></> : null}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="date" value={date || data?.date || ''} max={maxDate}
                onChange={(e) => { const v = e.target.value; if (v && v <= maxDate) setDate(v); }}
                className="px-2 py-1.5 text-xs border border-gray-200 rounded-xl bg-white"
                aria-label="Tarih"
              />
              {date && (
                <button
                  type="button" onClick={() => setDate(null)}
                  className="px-3 py-1.5 text-xs font-medium text-indigo-700 border border-indigo-200 rounded-xl hover:bg-indigo-50"
                >
                  Dün
                </button>
              )}
              <button
                type="button" onClick={() => fetchData(true)} disabled={loading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                Yenile
              </button>
              <button
                type="button" onClick={downloadXlsx} disabled={!data || exporting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50"
              >
                {exporting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                {exporting ? 'Hazırlanıyor…' : 'Excel'}
              </button>
            </div>
          </div>
        </div>

        {data && data.source?.finalized === false && (
          <div className="px-6 py-2 text-xs text-amber-800 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" />
            GA4 gün içi verisi, kesinleşmedi. Rakamlar gün boyunca değişir.
          </div>
        )}
        {error && <div className="px-6 py-4 text-sm text-rose-700">Veri alınamadı: {error}</div>}
        {loading && !data && <div className="px-6 py-10 text-sm text-gray-500">Yükleniyor…</div>}

        {s && (
          <div className={`grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 divide-x divide-gray-100 ${loading ? 'opacity-60' : ''}`}>
            <KpiCard label="Ciro" value={fmtMoney(s.revenue)} cur={s.revenue} prev={s.prevDay?.revenue} week={s.sameDayLastWeek?.revenue} fmt={fmtMoney} />
            <KpiCard label="Sipariş" value={fmtInt(s.orders)} cur={s.orders} prev={s.prevDay?.orders} week={s.sameDayLastWeek?.orders} fmt={fmtInt} />
            <KpiCard label="Adet" value={fmtInt(s.qty)} cur={s.qty} prev={s.prevDay?.qty} week={s.sameDayLastWeek?.qty} fmt={fmtInt} />
            <KpiCard label="Ort. sepet" value={fmtMoney(s.aov)} cur={s.aov} prev={s.prevDay?.aov} week={s.sameDayLastWeek?.aov} fmt={fmtMoney} />
            <KpiCard
              label="Harcama"
              value={s.spend?.available ? fmtMoney(s.spend.total) : '—'}
              sub={s.spend?.available
                ? `Google ${fmtMoney(s.spend.google)} · Meta ${fmtMoney(s.spend.meta)} · Criteo ${fmtMoney(s.spend.criteo)}${s.spend.other ? ` · Diğer ${fmtMoney(s.spend.other)}` : ''}`
                : 'Harcama verisi yok'}
              cur={s.spend?.total} prev={s.prevDay?.spend?.available ? s.prevDay.spend.total : null}
              week={s.sameDayLastWeek?.spend?.available ? s.sameDayLastWeek.spend.total : null} inverse fmt={fmtMoney}
            />
            <KpiCard label="ROAS" value={fmtRoas(s.roas)} cur={s.roas} prev={s.prevDay?.roas} week={s.sameDayLastWeek?.roas} fmt={fmtRoas} />
          </div>
        )}
      </Card>

      {data && (
        <Card className="rounded-2xl overflow-hidden">
          <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-sm font-semibold text-gray-900">Kanallar</h2>
            {noSalesChannels.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap text-xs">
                <span className="text-rose-700 font-semibold">Satış getirmeyen:</span>
                {noSalesChannels.map(c => (
                  <span key={c.channel} className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 font-medium">{c.channel}</span>
                ))}
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border-separate border-spacing-0">
              <thead>
                <tr className="text-xs text-gray-500">
                  <th className={`${th} text-left`}>Kanal</th>
                  <th className={`${th} text-right`}>Ciro</th>
                  <th className={`${th} text-right`}>Önceki gün</th>
                  <th className={`${th} text-right`}>Geçen hafta</th>
                  <th className={`${th} text-right`}>Sipariş</th>
                  <th className={`${th} text-right`}>Adet</th>
                  <th className={`${th} text-right`}>Ürün</th>
                  <th className={`${th} text-right`}>Harcama</th>
                  <th className={`${th} text-right`}>ROAS</th>
                </tr>
              </thead>
              <tbody>
                {channels.map(c => {
                  const none = c.noSales || !(c.revenue > 0);
                  const on = fChannel === c.channel;
                  return (
                    <tr
                      key={c.channel}
                      onClick={() => { if (!none) setFChannel(on ? '' : c.channel); }}
                      className={`${none ? 'bg-rose-50/60' : 'cursor-pointer hover:bg-gray-50'} ${on ? 'bg-indigo-50' : ''}`}
                      title={none ? 'Bu kanal satış getirmedi' : 'Ürün listesini bu kanala göre filtrele'}
                    >
                      <td className="px-3 py-2 border-b border-gray-100 font-medium text-gray-900 whitespace-nowrap">
                        {c.channel}
                        {none && <span className="ml-2 px-1.5 py-0.5 text-[10px] rounded bg-rose-100 text-rose-700 font-semibold">Satış yok</span>}
                      </td>
                      <td className="px-3 py-2 border-b border-gray-100 text-right tabular-nums whitespace-nowrap">
                        <span className={none ? 'text-rose-700' : 'font-semibold text-gray-900'}>{fmtMoney(c.revenue)}</span>
                      </td>
                      <td className="px-3 py-2 border-b border-gray-100 text-right whitespace-nowrap">
                        <Delta cur={c.revenue} prev={c.prevDay?.revenue} />
                        <span className="ml-1.5 text-[11px] text-gray-400 tabular-nums">{fmtMoney(c.prevDay?.revenue)}</span>
                      </td>
                      <td className="px-3 py-2 border-b border-gray-100 text-right whitespace-nowrap">
                        <Delta cur={c.revenue} prev={c.sameDayLastWeek?.revenue} />
                        <span className="ml-1.5 text-[11px] text-gray-400 tabular-nums">{fmtMoney(c.sameDayLastWeek?.revenue)}</span>
                      </td>
                      <td className="px-3 py-2 border-b border-gray-100 text-right tabular-nums">{fmtInt(c.orders)}</td>
                      <td className="px-3 py-2 border-b border-gray-100 text-right tabular-nums">{fmtInt(c.qty)}</td>
                      <td className="px-3 py-2 border-b border-gray-100 text-right tabular-nums">{fmtInt(c.products)}</td>
                      <td className="px-3 py-2 border-b border-gray-100 text-right tabular-nums whitespace-nowrap">{c.spend > 0 ? fmtMoney(c.spend) : '—'}</td>
                      <td className="px-3 py-2 border-b border-gray-100 text-right tabular-nums">{c.spend > 0 ? fmtRoas(c.roas) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {data && (
        <Card className="rounded-2xl overflow-hidden">
          <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Kırılımlar</h2>
            <span className="text-xs text-gray-500">Satıra tıklayınca ürün listesi filtrelenir</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
            <BreakdownTable title="Marka" rows={data.breakdowns?.brands || []} labelKey="brand" active={fBrand} onPick={setFBrand} />
            <BreakdownTable title="Cinsiyet" rows={data.breakdowns?.genders || []} labelKey="gender" active={fGender} onPick={setFGender} />
            <BreakdownTable title="Beden (numara)" rows={data.breakdowns?.sizes || []} labelKey="size" active={fSize} onPick={setFSize} showProducts={false} />
          </div>
        </Card>
      )}

      {data && (
        <Card className="rounded-2xl overflow-hidden">
          <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
            <h2 className="text-sm font-semibold text-gray-900 mr-2">Ürünler</h2>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                value={q} onChange={e => setQ(e.target.value)} placeholder="Ürün adı, kodu, renk…"
                className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-xl w-56 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
            <select value={fBrand} onChange={e => setFBrand(e.target.value)} className={selectCls}>
              <option value="">Tüm markalar</option>
              {filterOptions.brands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <select value={fChannel} onChange={e => setFChannel(e.target.value)} className={selectCls}>
              <option value="">Tüm kanallar</option>
              {channels.filter(c => c.revenue > 0).map(c => <option key={c.channel} value={c.channel}>{c.channel}</option>)}
            </select>
            <select value={fGender} onChange={e => setFGender(e.target.value)} className={selectCls}>
              <option value="">Tüm cinsiyetler</option>
              {filterOptions.genders.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            <select value={fSize} onChange={e => setFSize(e.target.value)} className={selectCls}>
              <option value="">Tüm bedenler</option>
              {sizeOptions.map(z => <option key={z} value={z}>{z}</option>)}
            </select>
            {filtered && (
              <button type="button" onClick={clearFilters} className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900">
                <X className="w-3 h-3" /> Filtreleri temizle
              </button>
            )}
            <span className="ml-auto text-xs text-gray-500 tabular-nums">
              {fmtInt(rows.length)}{rows.length !== products.length ? ` / ${fmtInt(products.length)}` : ''} ürün
              {filtered && <> · {fmtMoney(rows.reduce((a, p) => a + p.revenue, 0))}</>}
            </span>
          </div>

          {!rows.length ? (
            <div className="px-6 py-8 text-sm text-gray-500">{products.length ? 'Filtreye uyan ürün yok.' : 'Bu gün için satış verisi yok.'}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border-separate border-spacing-0">
                <thead>
                  <tr className="text-xs text-gray-500">
                    <th className={`${th} text-left min-w-[300px]`}>
                      <button type="button" onClick={() => onSort('name')} className="hover:text-gray-900">Ürün <SortMark k="name" /></button>
                    </th>
                    <th className={`${th} text-left`}>
                      <button type="button" onClick={() => onSort('brand')} className="hover:text-gray-900">Marka <SortMark k="brand" /></button>
                    </th>
                    <th className={`${th} text-left`}>
                      <button type="button" onClick={() => onSort('gender')} className="hover:text-gray-900">Cinsiyet <SortMark k="gender" /></button>
                    </th>
                    <th className={`${th} text-left`}>Renk</th>
                    <th className={`${th} text-left`}>Beden</th>
                    <th className={`${th} text-right`}>
                      <button type="button" onClick={() => onSort('qty')} className="hover:text-gray-900">Adet <SortMark k="qty" /></button>
                    </th>
                    <th className={`${th} text-right`}>
                      <button type="button" onClick={() => onSort('revenue')} className="hover:text-gray-900">Ciro <SortMark k="revenue" /></button>
                    </th>
                    <th className={`${th} text-right`}>
                      <button type="button" onClick={() => onSort('delta')} className="hover:text-gray-900" title="Önceki güne göre ciro değişimi">Önceki gün <SortMark k="delta" /></button>
                    </th>
                    <th className={`${th} text-left`}>Kanallar</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(p => {
                    const isTop = top5.has(p.itemId);
                    return (
                      <tr key={p.itemId} className={isTop ? 'bg-amber-50/50 hover:bg-amber-50' : 'hover:bg-gray-50'}>
                        <td className="px-3 py-2 border-b border-gray-100">
                          <div className="flex items-center gap-3">
                            <Thumb src={p.image} alt={p.name} onPreview={setPreview} />
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                {isTop && (
                                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-800 shrink-0" title="Günün ilk 5 ürünü">
                                    <Star className="w-3 h-3" /> İyi giden
                                  </span>
                                )}
                                {p.url ? (
                                  <a href={p.url} target="_blank" rel="noopener noreferrer" className="font-medium text-gray-900 hover:text-indigo-600 truncate max-w-[220px]" title={p.name}>
                                    {p.name}
                                  </a>
                                ) : (
                                  <span className="font-medium text-gray-900 truncate max-w-[220px]" title={p.name}>{p.name}</span>
                                )}
                                {p.url && <ExternalLink className="w-3 h-3 text-gray-400 shrink-0" />}
                              </div>
                              <div className="text-[11px] text-gray-400 mt-0.5">{[p.itemId, p.category].filter(Boolean).join(' · ')}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100 whitespace-nowrap">{p.brand || '—'}</td>
                        <td className="px-3 py-2 border-b border-gray-100 whitespace-nowrap">{p.gender || '—'}</td>
                        <td className="px-3 py-2 border-b border-gray-100 text-gray-700">{p.color || '—'}</td>
                        <td className="px-3 py-2 border-b border-gray-100 text-gray-700 tabular-nums">
                          {(p.sizes || []).length
                            ? p.sizes.map(z => `${z.size} ×${z.qty}`).join(', ')
                            : (p.size || '—')}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100 text-right tabular-nums">{fmtInt(p.qty)}</td>
                        <td className="px-3 py-2 border-b border-gray-100 text-right tabular-nums font-semibold text-gray-900 whitespace-nowrap" title={fmtMoney2(p.revenue)}>{fmtMoney(p.revenue)}</td>
                        <td className="px-3 py-2 border-b border-gray-100 text-right whitespace-nowrap">
                          {p.prevDay?.qty > 0 || p.prevDay?.revenue > 0
                            ? <><Delta cur={p.revenue} prev={p.prevDay?.revenue} /><div className="text-[11px] text-gray-400 tabular-nums">{fmtInt(p.prevDay?.qty)} adet</div></>
                            : <span className="text-[11px] text-gray-400">satış yoktu</span>}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100">
                          <div className="flex flex-wrap gap-1">
                            {(p.channels || []).map(c => (
                              <span
                                key={c.channel} title={`${c.channel}: ${fmtMoney(c.revenue)} · ${fmtInt(c.qty)} adet`}
                                className="px-1.5 py-0.5 text-[11px] rounded-md bg-indigo-50 text-indigo-800 border border-indigo-100 whitespace-nowrap"
                              >
                                {c.channel} · {fmtInt(c.qty)} adet · {fmtMoney(c.revenue)}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {rows.length > limit && (
                <div className="px-6 py-4 flex items-center justify-center gap-3 text-sm">
                  <span className="text-gray-500">{fmtInt(limit)} / {fmtInt(rows.length)} ürün</span>
                  <button type="button" onClick={() => setLimit(l => l + PAGE)} className="px-3 py-1.5 text-xs font-medium text-indigo-700 border border-indigo-200 rounded-xl hover:bg-indigo-50">
                    {PAGE} ürün daha göster
                  </button>
                  <button type="button" onClick={() => setLimit(rows.length)} className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50">
                    Tümünü göster
                  </button>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {data && (
        <div className="text-xs text-gray-500 space-y-1 px-1">
          <div className="flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              <div>{data.source?.note} {data.source?.revenueDefinition}.</div>
              <div>Bazı siparişlerde GA4 ürün satırı ciro göndermiyor; bu yüzden toplam Inbound raporundan düşük olabilir.</div>
              <div>Kanal kuralı: {data.source?.channelRule}</div>
              <div>Beden: {data.source?.sizeSource} · Cinsiyet: {data.source?.genderSource}</div>
              {data.coverage && (
                <div>
                  Kapsam: ciro kanal kaynağı oturum %{Math.round((data.coverage.session || 0) * 100)} · AEM %{Math.round((data.coverage.aem || 0) * 100)}
                  {data.coverage.collected ? ` · diğer kaynak %${Math.round(data.coverage.collected * 100)}` : ''}
                  {data.coverage.none ? ` · kanalı bilinmeyen %${Math.round(data.coverage.none * 100)}` : ''}
                  {' · '}beden bilinen adet %{Math.round((data.coverage.sizeKnownQtyShare || 0) * 100)}
                  {' · '}cinsiyet bilinen ürün {fmtInt(data.coverage.genderKnownProducts)}/{fmtInt(data.coverage.productsTotal)}
                  {' · '}görselli ürün {fmtInt(data.coverage.imageProducts)}/{fmtInt(data.coverage.productsTotal)}
                </div>
              )}
              {data.source?.generatedAt && (
                <div>
                  Hazırlandı: {new Date(data.source.generatedAt).toLocaleString('tr-TR')}{data.cached ? ' (önbellekten)' : ''}
                  {data.compareDates ? ` · kıyas: ${fmtShort(data.compareDates.prevDay)} ve ${fmtShort(data.compareDates.sameDayLastWeek)}` : ''}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {preview && (
        <img
          src={preview.src} alt="" aria-hidden="true"
          style={{ left: preview.x, top: Math.max(8, preview.y) }}
          className="fixed z-50 w-60 h-60 object-contain bg-white border border-gray-200 rounded-xl shadow-xl pointer-events-none"
        />
      )}
    </div>
  );
}
